import { useCallback, useEffect, useRef, useState } from 'react'
import { FAMILY_TZ, kickoffWithDate, windowLabel, zoneAbbrev } from '../../shared/clock'
import type { Side } from '../../shared/espn'
import { nextBoardWindow } from '../../shared/schedule'
import { api, type DraftGame, type DraftWeek } from '../api'

/*
 * Admin — the board, being built.
 *
 * The whole screen is one idea: a draft is PRIVATE. Nobody but the
 * commissioner has any way to learn that week 4 exists until he publishes it,
 * so this screen can afford to be slow, wrong and half-finished for two days
 * and cost nothing. That is why there is no autosave anxiety anywhere in it -
 * every action here is cheap because none of it is visible.
 *
 * The one thing that is NOT cheap is publish, and it is the only action given
 * a full-width bar.
 */

/**
 * How long a primed ✕ stays primed.
 *
 * Long enough to be a second thought, short enough that a phone put in a
 * pocket does not come back out still armed.
 */
const REVERT_MS = 4000

/**
 * A tap target the design draws smaller than a thumb.
 *
 * The approved rows put a 32px ✕ and a 32px SET LINE inside a 56px card, and
 * growing either to 44px would grow the card and lose the design. So the box
 * keeps its drawn size and a transparent ::after carries the hit area out to
 * 44px, which affects nothing else in the layout.
 *
 * `TAP_X` expands both ways (a 32×32 square); `TAP_ROW32` expands only
 * vertically, because those elements are already wider than 44 and expanding
 * them sideways would have them overlap the ✕ sitting 10px away.
 */
const TAP_X = "relative after:absolute after:-inset-1.5 after:content-['']"
const TAP_ROW32 = "relative after:absolute after:inset-x-0 after:-inset-y-1.5 after:content-['']"

/*
 * Sizes here are the PAINTED box, measured off the approved screenshots.
 * The design files size these controls content-box, so a "32px" chip with a
 * 1px border lands on screen at 34; this codebase is border-box, so 34 is
 * what has to be written down for the same 34 to come out.
 */
const CHIP = 'slate-num inline-flex min-h-[34px] shrink-0 items-center rounded-[3px]'

/**
 * The two-tap destructive pattern, in one place.
 *
 * It is used on the board and again on Add Game, and a confirm that reverts
 * after four seconds in one place and six in the other is not a pattern, it is
 * two bugs. Add Game imports this rather than keeping its own copy.
 */
export function useArmed(ms: number = REVERT_MS) {
  const [armed, setArmed] = useState<string | null>(null)
  const timer = useRef<number | undefined>(undefined)

  const arm = useCallback(
    (id: string | null) => {
      window.clearTimeout(timer.current)
      setArmed(id)
      // Only one thing is ever armed: arming a second ✕ disarms the first.
      if (id !== null) timer.current = window.setTimeout(() => setArmed(null), ms)
    },
    [ms],
  )

  // A pending revert on an unmounted screen sets state on nothing.
  useEffect(() => () => window.clearTimeout(timer.current), [])

  return [armed, arm] as const
}

/**
 * ✕, and then ✕ CONFIRM.
 *
 * No dialog. A dialog for removing one game from a private draft is heavier
 * than the mistake it prevents, and it puts a modal in front of an eight-year
 * old's parent on a phone. The confirm is ink, not red - there is no red in
 * this palette, and losing a game off an unpublished board is not an alarm.
 */
export function RemoveButton({
  armed,
  what,
  onArm,
  onRemove,
}: {
  armed: boolean
  what: string
  onArm: () => void
  onRemove: () => void
}) {
  if (armed) {
    return (
      <button
        type="button"
        onClick={onRemove}
        aria-label={`Confirm removing ${what}`}
        className={`${CHIP} ${TAP_ROW32} bg-ink px-2.5 text-[10px] font-bold tracking-[0.08em] text-ground`}
      >
        &#10005; CONFIRM
      </button>
    )
  }
  return (
    <button
      type="button"
      onClick={onArm}
      aria-label={`Remove ${what}`}
      className={`${CHIP} ${TAP_X} h-[34px] w-[34px] justify-center border border-hairline text-[13px] font-medium text-faint`}
    >
      &#10005;
    </button>
  )
}

/** AWAY AT HOME, which is how a football game is said out loud. */
function matchup(g: { home: { name: string }; away: { name: string } }) {
  return `${g.away.name} AT ${g.home.name}`.toUpperCase()
}

export function AdminBoard({
  onAddGame,
  onChanged,
  autoDraft = true,
  nextWeekNumber = 1,
  tz,
}: {
  onAddGame?: () => void
  onChanged?: () => void
  /**
   * `admin/draft` does not report the auto-draft setting, so the value comes
   * in from whoever loaded settings. The toggle then trusts the API's own
   * answer rather than its optimistic guess.
   */
  autoDraft?: boolean
  /** Used only when there is no draft at all and one has to be started. */
  nextWeekNumber?: number
  tz?: string
}) {
  const [week, setWeek] = useState<DraftWeek | null>(null)
  const [games, setGames] = useState<DraftGame[]>([])
  const [loaded, setLoaded] = useState(false)
  const [editing, setEditing] = useState<string | null>(null)
  const [auto, setAuto] = useState(autoDraft)
  const [note, setNote] = useState<string | null>(null)
  const [filling, setFilling] = useState(false)
  const [busy, setBusy] = useState(false)
  const [armed, arm] = useArmed()

  const load = useCallback(async () => {
    try {
      const d = await api.admin.draft.get()
      setWeek(d.week)
      setGames(d.games)
    } catch (e) {
      setNote(e instanceof Error ? e.message : 'Could not load the draft.')
    } finally {
      setLoaded(true)
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  const size = week?.board_size ?? 10
  const short = size - games.length

  const fill = async () => {
    if (!week) return
    setFilling(true)
    setNote(null)
    try {
      const r = await api.admin.draft.suggest(week.id)
      if (r.added === 0) setNote('Nothing left that fits - every remaining game shares a team with the board.')
      await load()
      onChanged?.()
    } catch (e) {
      setNote(e instanceof Error ? e.message : 'Could not choose games.')
    } finally {
      setFilling(false)
    }
  }
  const full = short <= 0

  const remove = async (id: string) => {
    arm(null)
    try {
      await api.admin.draft.remove(id)
      await load()
      onChanged?.()
    } catch (e) {
      setNote(e instanceof Error ? e.message : 'Could not remove that game.')
    }
  }

  const publish = async () => {
    if (!week) return
    setBusy(true)
    setNote(null)
    try {
      await api.admin.draft.publish(week.id)
      /*
       * `admin/draft` only ever returns a week in the DRAFT state, so a
       * successful publish empties this screen. The note is what is left to
       * say it went somewhere rather than nowhere.
       */
      setNote(`${week.label} is live. Everyone can see it now.`)
      await load()
      onChanged?.()
    } catch (e) {
      // A 409 here carries the server's count, which is more use than ours.
      setNote(e instanceof Error ? e.message : 'Could not publish.')
    } finally {
      setBusy(false)
    }
  }

  const toggleAuto = async () => {
    const next = !auto
    setAuto(next)
    try {
      const r = await api.admin.autoDraft(next)
      setAuto(r.on)
    } catch {
      setAuto(!next)
      setNote('Could not change auto-draft.')
    }
  }

  return (
    <div className="pb-5">
      <header className="flex items-baseline justify-between px-[18px] pt-5 pb-1">
        <span className="text-[30px] font-extrabold tracking-[0.03em] uppercase">
          Admin
        </span>
        <span className="slate-num text-right text-[10.5px] font-medium tracking-[0.1em] text-mute">
          {week ? `WEEK ${week.number} · DRAFT` : 'NO DRAFT'}
          {/*
           * The window, under the week number and on the screen where games
           * are chosen. It is the answer to "why is that game not on the
           * list" - a board can only ever hold what kicks off inside it.
           */}
          {week && (
            <span className="mt-0.5 block text-[9px] tracking-[0.06em] text-faint">
              {windowLabel(week.opens_at, week.closes_at, FAMILY_TZ)}
            </span>
          )}
        </span>
      </header>

      <p className="slate-num px-[18px] pb-3 text-[10px] tracking-[0.06em] text-mute uppercase">
        {week ? 'Players see nothing until you publish' : 'Nothing is being built right now'}
      </p>

      {week && (
        <>
          <ul className="flex flex-col gap-2 px-3 pb-2.5">
            {games.map((g, i) => (
              /* The 56px floor belongs on the CARD, not on the row inside
                 it: on the row it is 56 PLUS the card's two borders, and the
                 board comes out 2px per game taller than the design. */
              <li key={g.id} className="slate-card flex min-h-[56px] flex-col">
                <div className="flex flex-1 items-center gap-2.5 px-3 py-2.5">
                  {/*
                   * The position on the board, not `slot`. Slots keep their
                   * numbers when a game is removed, and a board reading
                   * 1 2 4 5 looks like a bug in the row nobody deleted.
                   */}
                  <span className="w-[18px] shrink-0 text-[18px] leading-none font-extrabold text-faint">
                    {i + 1}
                  </span>

                  <div className="flex min-w-0 flex-1 flex-col gap-[3px]">
                    <span className="text-[17px] leading-none font-bold tracking-[0.02em] text-ink uppercase">
                      {matchup(g)}
                    </span>
                    <span className="slate-num text-[9px] tracking-[0.06em] text-mute">
                      {[kickoffWithDate(g.kickoffAt, tz), g.networks[0]?.toUpperCase()]
                        .filter(Boolean)
                        .join(' · ')}
                    </span>
                  </div>

                  <LineControl
                    game={g}
                    open={editing === g.id}
                    onOpen={() => setEditing(editing === g.id ? null : g.id)}
                  />

                  <RemoveButton
                    armed={armed === g.id}
                    what={matchup(g)}
                    onArm={() => arm(g.id)}
                    onRemove={() => void remove(g.id)}
                  />
                </div>

                {editing === g.id && (
                  <LineEditor
                    game={g}
                    onDone={async () => {
                      setEditing(null)
                      await load()
                      onChanged?.()
                    }}
                    onFailed={setNote}
                  />
                )}
              </li>
            ))}

            {/*
             * FILL IT FOR ME, for a draft that is short.
             *
             * Starting a week suggests a board now, but a week started before
             * that did not, and dropping every game left no route back to the
             * selector but the Wednesday cron. It adds around whatever is
             * already there rather than replacing it - four games chosen by
             * hand are an opinion, not a starting point to be overwritten.
             */}
            {short > 0 && (
              <li>
                <button
                  type="button"
                  disabled={filling}
                  onClick={() => void fill()}
                  className="slate-num flex min-h-[55px] w-full items-center justify-center rounded-[6px] bg-ink text-[11px] font-bold tracking-[0.1em] text-ground"
                >
                  {filling ? 'CHOOSING…' : `SUGGEST ${short} GAME${short === 1 ? '' : 'S'}`}
                </button>
              </li>
            )}

            <li>
              <button
                type="button"
                onClick={onAddGame}
                /* No `slate-tap` here: it is unlayered, so its 44px
                   min-height would beat this 52px one. 52 already clears the
                   floor - the helper is for what the design draws smaller. */
                className="flex min-h-[55px] w-full items-center justify-center gap-2 rounded-[6px] border-[1.5px] border-dashed border-pale"
              >
                <span className="text-[20px] leading-none font-bold text-slate">+</span>
                <span className="slate-num text-[11px] font-semibold tracking-[0.1em] text-slate">
                  ADD GAME{short > 0 ? ` — ${short} MORE TO FILL` : ''}
                </span>
              </button>
            </li>
          </ul>

          {/*
           * The bar is recessed until the board is whole, and the count is IN
           * the label rather than beside it: "publish" with a number missing
           * is the only sentence this bar ever needs to say.
           */}
          <div className="px-3 pb-[18px]">
            <button
              type="button"
              disabled={!full || busy}
              onClick={() => void publish()}
              className={[
                'slate-num flex min-h-[48px] w-full items-center justify-center rounded-[6px] text-[12px] font-bold tracking-[0.12em]',
                full ? 'bg-ink text-ground' : 'bg-recessed text-faint',
              ].join(' ')}
            >
              {full
                ? `PUBLISH ${week.label.toUpperCase()} — ${size} GAMES`
                : `PUBLISH — NEEDS ${size} GAMES (${games.length} OF ${size})`}
            </button>
          </div>
        </>
      )}

      {loaded && !week && (
        <StartDraft
          number={nextWeekNumber}
          onCreated={async () => {
            await load()
            onChanged?.()
          }}
          onFailed={setNote}
        />
      )}

      {/*
       * The safety net, and it lives below publish on purpose: it is the thing
       * that happens if the commissioner never comes back to this screen.
       */}
      <div className="slate-card mx-3 mb-[18px] flex items-center gap-3 p-3">
        <div className="flex min-w-0 flex-1 flex-col gap-[3px]">
          <span className="slate-num text-[11px] font-semibold tracking-[0.08em] text-ink">
            AUTO-DRAFT &mdash; {auto ? 'ON' : 'OFF'}
          </span>
          <span className="slate-num text-[9px] leading-[1.6] tracking-[0.04em] text-mute">
            {/*
             * The hour is a setting, not an instant, so there is nothing for
             * formatKickoff to format - but the ZONE still has to be asked
             * rather than typed, and asked in the family's zone rather than
             * the viewer's, because that deadline is the same moment for
             * everybody and it moves an hour in November.
             */}
            IF NOT PUBLISHED BY WED 6:00 PM {zoneAbbrev(Date.now() / 1000, FAMILY_TZ)}, THE BOARD
            FILLS ITSELF: FAMILY TEAMS &rarr; TOP 25 &rarr; PRIMETIME NFL
          </span>
        </div>
        <button
          type="button"
          role="switch"
          aria-checked={auto}
          aria-label="Auto-draft"
          onClick={() => void toggleAuto()}
          className={[
            "relative h-5 w-[34px] shrink-0 rounded-[3px] after:absolute after:-inset-x-1.5 after:-inset-y-3 after:content-['']",
            auto ? 'bg-ink' : 'bg-recessed',
          ].join(' ')}
        >
          <span
            className={[
              'absolute top-[3px] h-3.5 w-3.5 rounded-[2px]',
              auto ? 'right-[3px] bg-ground' : 'left-[3px] bg-card',
            ].join(' ')}
          />
        </button>
      </div>

      {note && (
        <p className="slate-num px-[18px] text-[9px] leading-[1.6] tracking-[0.06em] text-mute">
          {note}
        </p>
      )}
    </div>
  )
}

/**
 * The line, or the offer to set one.
 *
 * NO LINE IS THE NORMAL STATE. In a mid-season week, 55 of 58 games had no
 * spread posted, so this control is a quiet bordered action and never a
 * warning: an empty board with ten SET LINEs on it is a Tuesday, not a
 * problem.
 */
function LineControl({
  game: g,
  open,
  onOpen,
}: {
  game: DraftGame
  open: boolean
  onOpen: () => void
}) {
  if (g.spread === null || g.favSide === null) {
    return (
      <button
        type="button"
        onClick={onOpen}
        aria-expanded={open}
        className={`${CHIP} ${TAP_ROW32} border-[1.5px] border-ink px-2.5 text-[10px] font-semibold tracking-[0.08em] text-ink`}
      >
        SET LINE
      </button>
    )
  }

  const fav = g.favSide === 'home' ? g.home.name : g.away.name
  const mine = g.lineSource === 'manual'

  return (
    <button
      type="button"
      onClick={onOpen}
      aria-expanded={open}
      /*
       * A line YOU typed and a line the books posted are different facts, and
       * the API keeps them apart, so the chip does too - dashed for yours.
       * Not a colour: gold would claim a player did it and slate is what a
       * posted number already is. A dashed edge is the same thing a dashed
       * chip means everywhere else here: provisional, and ours.
       */
      aria-label={
        mine
          ? `${fav} by ${g.spread}, your line. Edit it.`
          : `${fav} by ${g.spread}, posted. Edit it.`
      }
      className={[
        CHIP,
        TAP_ROW32,
        'border px-2 text-[11px] font-semibold text-slate',
        mine ? 'border-dashed border-pale' : 'border-hairline',
      ].join(' ')}
    >
      {fav.toUpperCase()} &minus;{g.spread}
    </button>
  )
}

/**
 * Setting a line: a magnitude, and which side it is against.
 *
 * A spread is stored as a positive number plus a favourite, never as a signed
 * number on one team, because "-3" alone has to be read together with whose
 * row it sits on and the two halves get separated the moment anything is
 * sorted or copied.
 */
function LineEditor({
  game: g,
  onDone,
  onFailed,
}: {
  game: DraftGame
  onDone: () => void
  onFailed: (m: string) => void
}) {
  const [mag, setMag] = useState(g.spread === null ? '' : String(g.spread))
  const [fav, setFav] = useState<Side | null>(g.favSide)
  const [busy, setBusy] = useState(false)

  const n = Number(mag)
  const valid = mag.trim() !== '' && Number.isFinite(n) && n > 0 && n <= 99 && fav !== null

  const save = async (spread: number | null, side: Side | null) => {
    setBusy(true)
    try {
      await api.admin.draft.line(g.id, spread, side)
      onDone()
    } catch (e) {
      onFailed(e instanceof Error ? e.message : 'Could not save that line.')
    } finally {
      setBusy(false)
    }
  }

  const side = (s: Side) => {
    const team = s === 'home' ? g.home : g.away
    const on = fav === s
    return (
      <button
        type="button"
        onClick={() => setFav(s)}
        aria-pressed={on}
        className={[
          'slate-num slate-tap flex-1 rounded-[3px] border px-2 text-[10px] font-semibold tracking-[0.08em]',
          on ? 'border-ink bg-ink text-ground' : 'border-hairline bg-card text-slate',
        ].join(' ')}
      >
        {team.name.toUpperCase()}
      </button>
    )
  }

  const label = 'slate-num w-[54px] shrink-0 text-[8.5px] tracking-[0.12em] text-faint'

  return (
    <div className="border-t border-hairline px-3 py-2.5">
      <div className="mb-2 flex items-center gap-2">
        <span className={label}>FAVORITE</span>
        {side('away')}
        {side('home')}
      </div>

      <div className="flex items-center gap-2">
        <span className={label}>POINTS</span>
        <input
          value={mag}
          onChange={(e) => setMag(e.target.value)}
          inputMode="decimal"
          aria-label="Points"
          className="slate-num slate-tap w-[72px] rounded-[3px] border border-hairline bg-ground px-2 text-center text-[13px] focus:border-gold-edge focus:outline-none"
        />
        <span className="flex-1" />
        {g.spread !== null && (
          <button
            type="button"
            disabled={busy}
            onClick={() => void save(null, null)}
            className={`${CHIP} ${TAP_ROW32} border border-hairline px-2.5 text-[10px] font-semibold tracking-[0.08em] text-slate`}
          >
            NO LINE
          </button>
        )}
        <button
          type="button"
          disabled={!valid || busy}
          onClick={() => void save(n, fav)}
          className={[
            CHIP,
            TAP_ROW32,
            'px-2.5 text-[10px] font-bold tracking-[0.08em]',
            valid ? 'bg-ink text-ground' : 'bg-recessed text-faint',
          ].join(' ')}
        >
          SAVE
        </button>
      </div>

      <p className="slate-num mt-2 text-[8.5px] tracking-[0.06em] text-faint">
        SAVED AS YOUR LINE, NOT THE BOOK&rsquo;S
      </p>
    </div>
  )
}

/**
 * There is no draft.
 *
 * Not in the approved screens, because the approved screens are of a board
 * mid-build - but it is where this screen starts every week, and where it
 * lands the moment a board is published. A window of kickoffs rather than an
 * ESPN week number: ESPN's college week 1 spans ten days.
 */
function StartDraft({
  number,
  onCreated,
  onFailed,
}: {
  number: number
  onCreated: () => void
  onFailed: (m: string) => void
}) {
  const [busy, setBusy] = useState(false)

  /*
   * NOTHING IS ASKED FOR. There used to be three inputs here - a week number
   * and two datetime pickers - and every one of them was a question the app
   * could answer better than a person. The number is just the last one plus
   * one. The window is a football week: Thursday night to Monday night.
   *
   * The number field was the worst of them, because it defaulted from state
   * and so quietly offered "2" for what was going to be the first week of the
   * season - a wrong answer presented as a suggestion, which is the kind a
   * person accepts.
   *
   * Recomputed on each render rather than held in state: this screen can sit
   * open across a Wednesday midnight, and a window pinned at mount would then
   * start a draft for a weekend that had already begun.
   */
  const { opensAt, closesAt } = nextBoardWindow(Math.floor(Date.now() / 1000))

  const create = async () => {
    setBusy(true)
    try {
      await api.admin.draft.create({
        number,
        label: `Week ${number}`,
        opensAt,
        closesAt,
      })
      onCreated()
    } catch (e) {
      onFailed(e instanceof Error ? e.message : 'Could not start a draft.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="slate-card mx-3 mb-[18px] p-3">
      <div className="mb-3 flex items-baseline justify-between gap-3">
        <span className="text-[24px] leading-none font-extrabold tracking-[0.03em] uppercase">
          Week {number}
        </span>
        <span className="slate-num text-right text-[10px] tracking-[0.08em] text-mute">
          {windowLabel(opensAt, closesAt, FAMILY_TZ)}
        </span>
      </div>

      <p className="slate-num mb-3 text-[9px] leading-[1.6] tracking-[0.06em] text-faint uppercase">
        Thursday night through Monday night. Games are chosen for you; you can
        add and drop any of them before you publish.
      </p>

      <button
        type="button"
        disabled={busy}
        onClick={() => void create()}
        className="slate-num flex min-h-[48px] w-full items-center justify-center rounded-[6px] bg-ink text-[12px] font-bold tracking-[0.12em] text-ground"
      >
        {busy ? 'STARTING\u2026' : `START WEEK ${number}`}
      </button>
    </div>
  )
}
